import { Button } from "@/components/Global/Button/Button";
import { LoaderCircle } from "@/components/Global/LoaderCircle/LoaderCircle";
import { useAppContext } from "@/context/AppContext";
import { RecipeType, StepZutatList } from "@/types/baseTypes";
import { setPdfTemplate } from "@/utils/rezeptAPI/pdfUtils";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import saveAs from "file-saver";
import React from "react";

export interface PrintListItem {
  item: RecipeType | StepZutatList;
  index: number;
  link?: string;
  base64?: string;
}

interface PDFDownloadButtonProps {
  printList: PrintListItem[];
  pdfType: "shopping-list" | "recipe";
}

function PDFDownloadButton({ printList, pdfType }: PDFDownloadButtonProps) {
  const { getLabel, getIcon } = useGlobalContext();
  const { loading, setLoading } = useAppContext();
  return (
    <>
      <Button
        className={`ghost`}
        type="primary"
        href="#"
        target={""}
        onClick={() =>
          onClickPdfDownload({
            getIcon,
            getLabel,
            printList,
            pdfType,
            setLoading,
          })
        }
        icon={"circleDownload"}
        aria-label={getLabel("aria_label_pdf_download_button")}
      >
        {getLabel("pdf_download")}
      </Button>
      {loading && <LoaderCircle position="relative" />}
    </>
  );
}

export default PDFDownloadButton;

export async function onClickPdfDownload({
  getIcon,
  getLabel,
  printList,
  pdfType,
  setLoading,
}: {
  getIcon: (iconName: string) => string;
  getLabel: (labelKey: string) => string;
  printList: PrintListItem[];
  pdfType: "shopping-list" | "recipe";
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const pdfElements = {
    icons: {
      completeTime: {
        src: getIcon("completeTime"),
        text: getLabel("complete_time"),
      },
      prepareTime: {
        src: getIcon("prepareTime"),
        text: getLabel("prepare_time"),
      },
      bakingTime: {
        src: getIcon("bakingTime"),
        text: getLabel("baking_time"),
      },
      oberunterHitze: {
        src: getIcon("oberunterHitze"),
        text: getLabel("recipe_label_ober_unterhitze"),
      },
      umluftIcon: {
        src: getIcon("umluft"),
        text: getLabel("recipe_label_umluft"),
      },
      cookingTime: {
        src: getIcon("cookingTime"),
        text: getLabel("cooking_time"),
      },
      coolingTime: {
        src: getIcon("coolingTime"),
        text: getLabel("cooling_time"),
      },
      restingTime: {
        src: getIcon("restingTime"),
        text: getLabel("resting_time"),
      },
      freezingTime: {
        src: getIcon("freezingTime"),
        text: getLabel("freezing_time"),
      },
    },
    logo: getIcon("kleineTiptopfLogo"),
  };
  async function addImagesAndLink() {
    const extendedWithPics = await Promise.all(
      printList.map(async (recipe) => {
        try {
          const result = await fetch(`/api/getImageForPDF`, {
            body: JSON.stringify({
              image_url: recipe.item.bild_mobile,
            }),
            headers: {
              "Content-Type": "application/json",
            },
            method: "POST",
          });
          const res = await result.json();
          return { ...recipe, ...res, link: window?.location?.href };
        } catch (e) {
          console.error(e);
        }
      }),
    );
    return extendedWithPics;
  }
  const onClickDownload = async () => {
    const pdf = (await import("@react-pdf/renderer")).pdf;
    setLoading(true);
    const printDataWithImages = await addImagesAndLink();
    const template = setPdfTemplate(pdfType, printDataWithImages, pdfElements);
    const pdfTitle =
      printDataWithImages[0]?.item?.rezept_titel || "tiptopf-rezept";
    if (!template) {
      console.error("PDF template not found");
      setLoading(false);
      return;
    }
    const blob = await pdf(template).toBlob();
    saveAs(blob, pdfTitle);
    setLoading(false);
  };

  await onClickDownload();
}
